import React, { useMemo } from 'react';

import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import type { AssetProcessingActivityPurposeApplication, AssetProcessingCountryRow } from 'config/api/assetRopa/assetRopa.types';
import { getRopaCountriesForUi, getRopaCountryClassification } from 'config/dictionaries/ropaCountryMasterdata';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';
import {
  APPLICABILITY_STATUS_OPTIONS,
  COUNTRY_VOLUME_OPTIONS,
  DPIA_INDICATION_STATUS_OPTIONS,
  MINIMUM_SECURITY_ASSESSMENT_OPTIONS,
  RETENTION_UNIT_OPTIONS,
  type AssetProcessingApaFormValues,
} from '../assetProcessingApa.types';
import type { CentralProcessingActivityPurpose } from '../ProcessingActivitySidePanel/ProcessingActivitySidePanel';

import { APA_EMPTY_VALUE, APA_MULTILINE_MAX_ROWS, ApaReadField, ApaRetentionReadFields } from './ApaReadField';

type SectionProps = {
  errors?: Partial<Record<string, string>>;
  onChange: (values: AssetProcessingApaFormValues) => void;
  readOnly?: boolean;
  values: AssetProcessingApaFormValues;
};

const COUNTRY_HANDLING_FLAGS = ['storage', 'access', 'support', 'development'] as const;

const getOptionLabel = (namespace: Record<string, string[]>, key: string, t: (message: string | string[]) => string) => {
  const message = namespace[key];
  return message ? t(message) : key.replace(/_/g, ' ');
};

const isRetentionUnitOption = (value: string): value is typeof RETENTION_UNIT_OPTIONS[number] =>
  (RETENTION_UNIT_OPTIONS as readonly string[]).includes(value);

export const AssetProcessingSecuritySection: React.FC<SectionProps> = ({ errors = {}, onChange, readOnly = false, values }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_editor.sections.security;

  return (
    <section className={styles.apaSection}>
      <Typography gutterBottom variant='h4'>
        {t(messages.title)}
      </Typography>
      {readOnly ? (
        <div className={styles.fieldList}>
          <ApaReadField label={t(messages.comment)} value={values.securityComment} />
          <ApaReadField
            label={t(messages.minimum_assessment)}
            preserveLineBreaks={false}
            value={getOptionLabel(messages.minimum_assessment_options, values.minimumSecurityAssessment, t)}
          />
          <ApaReadField label={t(messages.minimum_assessment_comment)} value={values.minimumSecurityAssessmentComment} />
        </div>
      ) : (
        <>
          <TextField
            fullWidth
            label={t(messages.comment)}
            margin='normal'
            maxRows={APA_MULTILINE_MAX_ROWS}
            minRows={4}
            multiline
            onChange={event => onChange({ ...values, securityComment: event.target.value })}
            value={values.securityComment}
            variant='outlined'
          />
          <FormControl fullWidth margin='normal' variant='outlined'>
            <InputLabel>{t(messages.minimum_assessment)}</InputLabel>
            <Select
              label={t(messages.minimum_assessment)}
              onChange={event => onChange({ ...values, minimumSecurityAssessment: String(event.target.value) })}
              value={values.minimumSecurityAssessment}
            >
              {MINIMUM_SECURITY_ASSESSMENT_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>
                  {getOptionLabel(messages.minimum_assessment_options, option, t)}
                </MenuItem>
              ))}
            </Select>
            {errors.minimumSecurityAssessment && <FormHelperText error>{errors.minimumSecurityAssessment}</FormHelperText>}
          </FormControl>
          <TextField
            error={Boolean(errors.minimumSecurityAssessmentComment)}
            fullWidth
            helperText={errors.minimumSecurityAssessmentComment}
            label={t(messages.minimum_assessment_comment)}
            margin='normal'
            maxRows={APA_MULTILINE_MAX_ROWS}
            minRows={3}
            multiline
            onChange={event => onChange({ ...values, minimumSecurityAssessmentComment: event.target.value })}
            value={values.minimumSecurityAssessmentComment}
            variant='outlined'
          />
        </>
      )}
    </section>
  );
};

type DpiaLogSectionProps = {
  recordActions?: React.ReactNode;
  downloadDisabled?: boolean;
  downloadDisabledReason?: string;
  downloading?: boolean;
  fileName: string;
  onDownload: () => void;
};

export const AssetProcessingDpiaLogSection: React.FC<DpiaLogSectionProps> = ({
  downloadDisabled = false,
  downloadDisabledReason,
  downloading = false,
  fileName,
  onDownload,
  recordActions,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_editor.sections.dpia_log;
  const isDownloadDisabled = downloading || downloadDisabled;
  const downloadTooltip = (downloadDisabled ? downloadDisabledReason ?? t(messages.download_unavailable) : t(messages.download)) ?? '';

  return (
    <section className={styles.apaSection}>
      <Typography gutterBottom variant='h4'>
        {t(messages.title)}
      </Typography>
      <div className={styles.dpiaLogRow}>
        <Typography>{fileName}</Typography>
        <Tooltip title={downloadTooltip}>
          <span>
            <IconButton aria-label={t(messages.download)} disabled={isDownloadDisabled} onClick={onDownload}>
              <DownloadIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      {recordActions}
    </section>
  );
};

export const AssetProcessingDpiaSection: React.FC<SectionProps> = ({ errors = {}, onChange, readOnly = false, values }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_editor.sections.dpia;

  return (
    <section className={styles.apaSection}>
      <Typography gutterBottom variant='h4'>
        {t(messages.title)}
      </Typography>
      {readOnly ? (
        <div className={styles.fieldList}>
          <ApaReadField
            label={t(messages.status)}
            preserveLineBreaks={false}
            value={getOptionLabel(messages.status_options, values.dpiaIndicationStatus, t)}
          />
          <ApaReadField label={t(messages.comment)} value={values.dpiaIndicationComment} />
        </div>
      ) : (
        <>
          <FormControl fullWidth margin='normal' variant='outlined'>
            <InputLabel>{t(messages.status)}</InputLabel>
            <Select
              label={t(messages.status)}
              onChange={event => onChange({ ...values, dpiaIndicationStatus: String(event.target.value) })}
              value={values.dpiaIndicationStatus}
            >
              {DPIA_INDICATION_STATUS_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>
                  {getOptionLabel(messages.status_options, option, t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            error={Boolean(errors.dpiaIndicationComment)}
            fullWidth
            helperText={errors.dpiaIndicationComment}
            label={t(messages.comment)}
            margin='normal'
            maxRows={APA_MULTILINE_MAX_ROWS}
            minRows={3}
            multiline
            onChange={event => onChange({ ...values, dpiaIndicationComment: event.target.value })}
            value={values.dpiaIndicationComment}
            variant='outlined'
          />
        </>
      )}
    </section>
  );
};

type PurposeSectionProps = SectionProps & {
  centralPurposes: CentralProcessingActivityPurpose[];
};

export const AssetProcessingPurposeApplicationsSection: React.FC<PurposeSectionProps> = ({
  centralPurposes,
  errors = {},
  onChange,
  readOnly = false,
  values,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_editor.sections.purposes;

  const updatePurpose = (index: number, patch: Partial<AssetProcessingActivityPurposeApplication>) => {
    const next = values.purposeApplications.map((purpose, purposeIndex) => (purposeIndex === index ? { ...purpose, ...patch } : purpose));
    onChange({ ...values, purposeApplications: next });
  };

  return (
    <section className={styles.apaSection}>
      <Typography gutterBottom variant='h4'>
        {t(messages.title)}
      </Typography>
      {values.purposeApplications.map((purpose, index) => {
        const central = centralPurposes.find(item => item.id === purpose.processingPurposeId);
        return (
          <details key={purpose.id} className={styles.purposePanel} open>
            <summary className={styles.purposeSummary}>{purpose.name}</summary>
            <div className={styles.purposePanelBody}>
              {central && (
                <div className={styles.purposeCentralBlock}>
                  <Typography variant='h5'>{t(messages.central_definition)}</Typography>
                  <div className={styles.fieldList}>
                    <ApaReadField label={t(messages.legal_basis)} preserveLineBreaks={false} value={central.legalBasis} />
                    <ApaRetentionReadFields
                      retentionComment={central.retentionComment}
                      retentionTrigger={central.retentionTrigger}
                      retentionUnit={central.retentionUnit}
                      retentionValue={central.retentionValue}
                    />
                  </div>
                </div>
              )}
              <div className={styles.purposeAssetBlock}>
                <Typography variant='h5'>{t(messages.asset_application)}</Typography>
                {readOnly ? (
                  <div className={styles.fieldList}>
                    <ApaReadField
                      label={t(messages.applicability)}
                      preserveLineBreaks={false}
                      value={getOptionLabel(messages.applicability_options, purpose.applicabilityStatus, t)}
                    />
                    {(purpose.applicabilityStatus === 'applies' || purpose.applicabilityStatus === 'does_not_apply') && (
                      <ApaReadField label={t(messages.justification)} value={purpose.justification} />
                    )}
                    {purpose.applicabilityStatus === 'applies' && (
                      <ApaRetentionReadFields
                        deletionRoutine={purpose.deletionRoutine}
                        retentionComment={purpose.retentionComment}
                        retentionTrigger={purpose.retentionTrigger}
                        retentionUnit={purpose.retentionUnit}
                        retentionValue={purpose.retentionValue}
                        showDeletionRoutine
                      />
                    )}
                  </div>
                ) : (
                  <>
                    <FormControl fullWidth margin='normal' variant='outlined'>
                      <InputLabel>{t(messages.applicability)}</InputLabel>
                      <Select
                        label={t(messages.applicability)}
                        onChange={event =>
                          updatePurpose(index, {
                            applicabilityStatus: event.target.value as AssetProcessingActivityPurposeApplication['applicabilityStatus'],
                          })
                        }
                        value={purpose.applicabilityStatus}
                      >
                        {APPLICABILITY_STATUS_OPTIONS.map(option => (
                          <MenuItem key={option} value={option}>
                            {getOptionLabel(messages.applicability_options, option, t)}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors[`purpose.${index}.applicabilityStatus`] && (
                        <FormHelperText error>{errors[`purpose.${index}.applicabilityStatus`]}</FormHelperText>
                      )}
                      {errors[`purpose.${index}.applies`] && <FormHelperText error>{errors[`purpose.${index}.applies`]}</FormHelperText>}
                    </FormControl>
                    {(purpose.applicabilityStatus === 'applies' || purpose.applicabilityStatus === 'does_not_apply') && (
                      <TextField
                        error={Boolean(errors[`purpose.${index}.justification`])}
                        fullWidth
                        helperText={errors[`purpose.${index}.justification`]}
                        label={t(messages.justification)}
                        margin='normal'
                        maxRows={APA_MULTILINE_MAX_ROWS}
                        minRows={2}
                        multiline
                        onChange={event => updatePurpose(index, { justification: event.target.value })}
                        value={purpose.justification}
                        variant='outlined'
                      />
                    )}
                    {purpose.applicabilityStatus === 'applies' && (
                      <>
                        <TextField
                          fullWidth
                          label={t(messages.retention_trigger)}
                          margin='normal'
                          onChange={event => updatePurpose(index, { retentionTrigger: event.target.value })}
                          value={purpose.retentionTrigger}
                          variant='outlined'
                        />
                        <TextField
                          error={Boolean(errors[`purpose.${index}.retentionValue`])}
                          fullWidth
                          helperText={errors[`purpose.${index}.retentionValue`]}
                          inputProps={{ min: 0, step: 1 }}
                          label={t(messages.retention_value)}
                          margin='normal'
                          onChange={event => {
                            const nextValue = event.target.value.trim();
                            updatePurpose(index, { retentionValue: nextValue ? Number(nextValue) : null });
                          }}
                          type='number'
                          value={purpose.retentionValue ?? ''}
                          variant='outlined'
                        />
                        <FormControl error={Boolean(errors[`purpose.${index}.retentionUnit`])} fullWidth margin='normal' variant='outlined'>
                          <InputLabel>{t(messages.retention_unit)}</InputLabel>
                          <Select
                            label={t(messages.retention_unit)}
                            onChange={event => updatePurpose(index, { retentionUnit: String(event.target.value) })}
                            value={isRetentionUnitOption(purpose.retentionUnit) ? purpose.retentionUnit : ''}
                          >
                            <MenuItem value=''>—</MenuItem>
                            {RETENTION_UNIT_OPTIONS.map(option => (
                              <MenuItem key={option} value={option}>
                                {t(processing_activities_messages.editor.retention_units[option])}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors[`purpose.${index}.retentionUnit`] && (
                            <FormHelperText>{errors[`purpose.${index}.retentionUnit`]}</FormHelperText>
                          )}
                        </FormControl>
                        <TextField
                          fullWidth
                          label={t(messages.retention_comment)}
                          margin='normal'
                          maxRows={APA_MULTILINE_MAX_ROWS}
                          minRows={2}
                          multiline
                          onChange={event => updatePurpose(index, { retentionComment: event.target.value })}
                          value={purpose.retentionComment}
                          variant='outlined'
                        />
                        <TextField
                          fullWidth
                          label={t(messages.deletion_routine)}
                          margin='normal'
                          maxRows={APA_MULTILINE_MAX_ROWS}
                          minRows={2}
                          multiline
                          onChange={event => updatePurpose(index, { deletionRoutine: event.target.value })}
                          value={purpose.deletionRoutine}
                          variant='outlined'
                        />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </details>
        );
      })}
    </section>
  );
};

export const AssetProcessingCountryRowsSection: React.FC<SectionProps> = ({ errors = {}, onChange, readOnly = false, values }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_editor.sections.countries;
  const countries = useMemo(() => getRopaCountriesForUi(), []);

  const updateRow = (index: number, patch: Partial<AssetProcessingCountryRow>) => {
    const next = values.countryRows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row));
    onChange({ ...values, countryRows: next });
  };

  const addRow = () => {
    onChange({
      ...values,
      countryRows: [
        ...values.countryRows,
        {
          id: Date.now(),
          countryCode: '',
          countryName: '',
          classification: '',
          storage: false,
          access: false,
          support: false,
          development: false,
          volume: '',
        },
      ],
    });
  };

  const removeRow = (index: number) => {
    onChange({ ...values, countryRows: values.countryRows.filter((_, rowIndex) => rowIndex !== index) });
  };

  return (
    <section className={styles.apaSection}>
      <Typography gutterBottom variant='h4'>
        {t(messages.title)}
      </Typography>
      {errors.countries && <FormHelperText error>{errors.countries}</FormHelperText>}
      {values.countryRows.map((row, index) => {
        const handlingLabels = COUNTRY_HANDLING_FLAGS.filter(flag => row[flag]).map(flag => t(messages.handling[flag]));

        return (
          <div key={row.id} className={styles.countryRowCard}>
            {readOnly ? (
              <div className={styles.fieldList}>
                <ApaReadField label={t(messages.country_name)} preserveLineBreaks={false} value={row.countryName} />
                <ApaReadField
                  label={t(messages.handling_types)}
                  preserveLineBreaks={false}
                  value={handlingLabels.length > 0 ? handlingLabels.join(', ') : APA_EMPTY_VALUE}
                />
                <ApaReadField
                  label={t(messages.volume)}
                  preserveLineBreaks={false}
                  value={row.volume ? getOptionLabel(messages.volume_options, row.volume, t) : APA_EMPTY_VALUE}
                />
              </div>
            ) : (
              <>
                <FormControl fullWidth margin='normal' variant='outlined'>
                  <InputLabel>{t(messages.country_name)}</InputLabel>
                  <Select
                    label={t(messages.country_name)}
                    onChange={event => {
                      const countryCode = String(event.target.value);
                      const country = countries.find(item => item.iso2 === countryCode);
                      updateRow(index, {
                        countryCode,
                        countryName: country?.name ?? countryCode,
                        classification: getRopaCountryClassification(countryCode),
                      });
                    }}
                    value={row.countryCode}
                  >
                    {countries.map(country => (
                      <MenuItem key={country.iso2} value={country.iso2}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormGroup row>
                  {COUNTRY_HANDLING_FLAGS.map(flag => (
                    <FormControlLabel
                      key={flag}
                      control={<Checkbox checked={row[flag]} onChange={event => updateRow(index, { [flag]: event.target.checked })} />}
                      label={t(messages.handling[flag])}
                    />
                  ))}
                </FormGroup>
                <FormControl fullWidth margin='normal' variant='outlined'>
                  <InputLabel>{t(messages.volume)}</InputLabel>
                  <Select
                    label={t(messages.volume)}
                    onChange={event => updateRow(index, { volume: String(event.target.value) })}
                    value={row.volume}
                  >
                    {COUNTRY_VOLUME_OPTIONS.map(option => (
                      <MenuItem key={option} value={option}>
                        {getOptionLabel(messages.volume_options, option, t)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors[`country.${index}`] && <FormHelperText error>{errors[`country.${index}`]}</FormHelperText>}
                <button className={styles.textButtonLink} onClick={() => removeRow(index)} type='button'>
                  {t(messages.remove_row)}
                </button>
              </>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <button className={styles.textButtonLink} onClick={addRow} type='button'>
          {t(messages.add_row)}
        </button>
      )}
    </section>
  );
};
